import { api } from "./api";
import { renderChrome } from "./chrome";
import { renderAskHubFab } from "./components/assistant";
import { el } from "./lib/dom";
import { heroBand } from "./lib/ui";
import { navigate, Router } from "./router";
import { pendingRequests } from "./session";
import type { WhoAmI } from "./types";
import { renderAdmin } from "./views/admin";
import { renderAppDetail } from "./views/app_detail";
import { renderFeedback } from "./views/feedback";
import { renderHub } from "./views/hub";
import { renderLogin } from "./views/login";
import { renderMaintenance } from "./views/maintenance";

const appRoot = document.getElementById("app");
if (!appRoot) throw new Error("#app mount point missing");

let me: WhoAmI | null = null;

function shell(view: HTMLElement): void {
  appRoot!.replaceChildren();
  if (me) appRoot!.append(renderChrome(me));
  appRoot!.append(view);
}

function forbidden(): HTMLElement {
  return el("main", { class: "page" }, [
    heroBand("Not", "authorised", "You don't have access to this area."),
  ]);
}

async function guarded(render: () => HTMLElement | Promise<HTMLElement>): Promise<void> {
  if (!me) {
    shell(renderLogin());
    return;
  }
  shell(await render());
}

const router = new Router()
  .add("/", () => guarded(renderHub))
  .add("/apps/:key", (p) => guarded(() => renderAppDetail(p.key)))
  .add("/feedback", () => guarded(renderFeedback))
  .add("/admin", () => guarded(() => (me!.is_admin ? renderAdmin("approvals") : forbidden())))
  .add("/admin/team", () => guarded(() => (me!.is_admin ? renderAdmin("team") : forbidden())))
  .add("/admin/apps", () => guarded(() => (me!.is_admin ? renderAdmin("apps") : forbidden())))
  .add("/admin/feedback", () => guarded(() => (me!.is_admin ? renderAdmin("feedback") : forbidden())))
  .add("/admin/insights", () => guarded(() => (me!.is_admin ? renderAdmin("insights") : forbidden())))
  .add("/admin/health", () => guarded(() => (me!.is_admin ? renderAdmin("health") : forbidden())))
  .add("/login", () => {
    if (me) navigate("/");
    else shell(renderLogin());
  });

async function boot(): Promise<void> {
  const [meRes, maintRes] = await Promise.allSettled([api.whoami(), api.getMaintenance()]);
  me = meRes.status === "fulfilled" ? meRes.value : null;
  if (me) pendingRequests.set(me.pending_access_requests ?? 0);
  const maint = maintRes.status === "fulfilled" ? maintRes.value : null;

  // Hub-level maintenance: everyone (incl. logged-out visitors) sees the notice and
  // can't log in — EXCEPT super-admins, who bypass so they can still operate Hub.
  if (maint?.hub.on && !me?.is_admin) {
    appRoot!.replaceChildren(renderMaintenance(maint.hub.message));
    return;
  }
  router.start();
  if (me) document.body.appendChild(renderAskHubFab());
  // Super-admin bypassing an active maintenance window — make it obvious.
  if (maint?.hub.on && me?.is_admin) {
    document.body.appendChild(
      el("div", { class: "maintenance-banner" }, [
        `Hub is in MAINTENANCE — visible to super-admins only. ${maint.hub.message ?? ""}`,
      ]),
    );
  }
}

void boot();
